import {
  BigQueryComparison,
  BigQuerySpecialComparison,
  WhereOperation,
} from '@principle-theorem/reporting/interfaces';
import {
  DocumentReference,
  ISODateTimeType,
  Timestamp,
  isISODateTimeType,
  serialise,
} from '@principle-theorem/shared';
import { isNumber, isString } from 'lodash';
import { UNDEFINED_VALUE } from './query-option-resolvers';

export class BigQuerySQL {
  static or(statements: string[]): string {
    return `(${statements.join(` ${WhereOperation.Or} `)})`;
  }

  static and(statements: string[]): string {
    return `(${statements.join(` ${WhereOperation.And} `)})`;
  }

  static filter(
    path: string,
    comparison: BigQueryComparison | BigQuerySpecialComparison,
    value: string | number | boolean | undefined,
    wrapStringInQuotes: boolean = true
  ): string {
    if (value === undefined || value === UNDEFINED_VALUE) {
      if (comparison === 'IS' || comparison === 'IS NOT') {
        return `${path} ${comparison} NULL`;
      }
      return `${path} IS NULL`;
    }
    if (isNumber(value)) {
      return `${path} ${comparison} ${value}`;
    }
    if (isString(value)) {
      switch (comparison) {
        case 'NORMALIZE_AND_CASEFOLD':
          return `NORMALIZE_AND_CASEFOLD(${path}) = NORMALIZE_AND_CASEFOLD("${value}")`;
        case 'CONTAINS_SUBSTR':
          return `CONTAINS_SUBSTR(${path}, "${value}")`;
        case 'NOT CONTAINS_SUBSTR':
          return `NOT CONTAINS_SUBSTR(${path}, "${value}")`;
        case 'STARTS_WITH':
          return `STARTS_WITH(${path}, "${value}")`;
        case 'ENDS_WITH':
          return `ENDS_WITH(${path}, "${value}")`;
        default:
          if (wrapStringInQuotes) {
            return `${path} ${comparison} "${value}"`;
          }
          return `${path} ${comparison} ${value}`;
      }
    }
    return `${path} ${comparison} ${value.toString()}`;
  }

  static docRef<T>(
    path: string,
    comparison: '=' | '>=' | '<=',
    value: DocumentReference<T>
  ): string {
    return this.filter(path, comparison, serialise(value).referenceValue);
  }

  static timestamp(
    path: string,
    comparison: '=' | '>=' | '<=' | '>' | '<',
    value: Timestamp
  ): string {
    return BigQuerySQL.filter(
      BigQuerySQL.castTimestamp(path, true),
      comparison,
      BigQuerySQL.castTimestamp(serialise(value).timestampValue, false),
      false
    );
  }

  static dateRange(
    path: string,
    from: ISODateTimeType,
    to: ISODateTimeType
  ): string {
    const fromFilter = BigQuerySQL.filter(
      BigQuerySQL.castTimestamp(path, true),
      '>=',
      BigQuerySQL.castTimestamp(from, false),
      false
    );
    const toFilter = BigQuerySQL.filter(
      BigQuerySQL.castTimestamp(path, true),
      '<=',
      BigQuerySQL.castTimestamp(to, false),
      false
    );
    return this.and([fromFilter, toFilter]);
  }

  static castTimestamp(
    rawValue: string,
    isPropertyPath: boolean = false
  ): string {
    if (isPropertyPath) {
      return `TIMESTAMP(${rawValue})`;
    }

    if (!isISODateTimeType(rawValue)) {
      // eslint-disable-next-line no-console
      console.error(
        `BigQuerySQL.castTimestamp was passed a string that is not an ISODateTimeType: ${String(
          rawValue
        )}`
      );
    }

    const value = isPropertyPath ? rawValue : `"${rawValue}"`;
    return `TIMESTAMP(${value})`;
  }

  static objectInArray(
    unnestPaths: IUnnestPath[],
    whereCondition: string,
    notInArray: boolean
  ): string {
    // TODO: add undefined handling. eg patients that havent seen Dr Jordan, should still show patients that haven't seen anyone.
    const unnests = unnestPaths
      .map((unnest) => {
        return `UNNEST(${unnest.path}) AS ${unnest.alias}`;
      })
      .join(', ');
    const inArrayQuery = `EXISTS(SELECT 1 FROM ${unnests} WHERE ${whereCondition})`;
    return notInArray ? `NOT ${inArrayQuery}` : inArrayQuery;
  }
}

export interface IUnnestPath {
  path: string;
  alias: string;
}
